import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Subscription, take, filter } from 'rxjs';
import { AppState } from '../../state/reducers';
import { InputFieldComponent } from '../../common/form/input-field/input-field.component';
import { ComboboxMultiselectComponent } from '../../common/form/combobox-multiselect/combobox-multiselect.component';
import { PopupService } from '../../services/popup.service';
import { OnboardingService } from '../../services/onboarding.service';
import { environment } from '../../../environments/environment';

import * as OnboardingActions from '../../state/onboarding/onboarding.actions';
import * as OnboardingSelectors from '../../state/onboarding/onboarding.selectors';
import { ExtractionStatus, OnboardingTargetingData } from '../../state/onboarding/onboarding.state';
import { MAX_CUSTOM_TARGET_LENGTH, OTHER_OPTION_ID, TARGET_JOB_TITLE_OPTIONS, isPredefinedOption, sanitizeCustomTarget } from '../../shared/icp-targeting-options';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputFieldComponent,
    ComboboxMultiselectComponent
  ],
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss']
})
export class OnboardingComponent implements OnInit, OnDestroy {
  step: number = 1;
  form: FormGroup;
  isLoading: boolean = false;

  private subscriptions = new Subscription();

  // Onboarding session observables
  sessionId$ = this.store.select(OnboardingSelectors.selectOnboardingSessionId);
  sessionLoading$ = this.store.select(OnboardingSelectors.selectOnboardingSessionLoading);
  extractionStatus$ = this.store.select(OnboardingSelectors.selectExtractionStatus);
  extractionLoading$ = this.store.select(OnboardingSelectors.selectExtractionLoading);
  isExtractionComplete$ = this.store.select(OnboardingSelectors.selectIsExtractionComplete);
  isExtractionInProgress$ = this.store.select(OnboardingSelectors.selectIsExtractionInProgress);
  isExtractionFailed$ = this.store.select(OnboardingSelectors.selectIsExtractionFailed);
  productDescription$ = this.store.select(OnboardingSelectors.selectProductDescription);
  extractionCompanyName$ = this.store.select(OnboardingSelectors.selectExtractionCompanyName);
  extractionBenefits$ = this.store.select(OnboardingSelectors.selectExtractionBenefits);
  extractionPains$ = this.store.select(OnboardingSelectors.selectExtractionPains);
  updateStepLoading$ = this.store.select(OnboardingSelectors.selectUpdateStepLoading);
  completeLoading$ = this.store.select(OnboardingSelectors.selectCompleteLoading);
  completeSuccess$ = this.store.select(OnboardingSelectors.selectCompleteSuccess);

  // Step 2 options
  targetJobTitleOptions = TARGET_JOB_TITLE_OPTIONS;

  selectedJobTitles: Set<string> = new Set();

  // Countries + Industries from API
  countries: any[] = [];
  industries: any[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private http: HttpClient,
    private popupService: PopupService,
    private onboardingService: OnboardingService
  ) {
    this.form = this.formBuilder.group({
      // Step 1 fields
      website_url: ['', [Validators.required, Validators.pattern(/^(https?:\/\/)?(www\.)?[\w-]+\.[\w]{2,}.*$/)]],
      company_name: [''],

      // Step 2 fields
      target_industries: [[]],
      target_job_titles: [[]],
      target_countries: [[]]
    });
  }

  ngOnInit(): void {
    // Load supporting data
    this.loadCountries();
    this.loadIndustries();

    // Initialize or resume session
    this.initOrResumeSession();

    // Subscribe to complete success for navigation
    this.subscribeToCompleteSuccess();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ─── Session Management ─────────────────────────────────────────────

  private initOrResumeSession(): void {
    const savedSession = this.onboardingService.getSessionFromStorage();

    if (savedSession && this.onboardingService.isSessionValid()) {
      // Resume existing session
      this.store.dispatch(OnboardingActions.resumeOnboardingSession({
        sessionId: savedSession.sessionId
      }));

      // Restore step
      if (savedSession.currentStep > 0) {
        this.step = savedSession.currentStep;
      }

      // Restore form from step data
      const resumeSub = this.store.select(OnboardingSelectors.selectOnboardingStepData)
        .pipe(filter(data => !!data && Object.keys(data).length > 0), take(1))
        .subscribe(stepData => {
          this.restoreFormFromStepData(stepData);
        });
      this.subscriptions.add(resumeSub);
    } else {
      // Initialize new session
      this.store.dispatch(OnboardingActions.initOnboardingSession());
    }
  }

  private restoreFormFromStepData(stepData: any): void {
    if (stepData.step1) {
      this.form.patchValue({
        website_url: stepData.step1.website_url || '',
        company_name: stepData.step1.company_name || ''
      });
    }

    if (stepData.step2) {
      this.form.patchValue({
        target_industries: stepData.step2.target_industries || [],
        target_job_titles: stepData.step2.target_job_titles || [],
        target_countries: stepData.step2.target_countries || []
      });

      // Restore selected job titles
      if (stepData.step2.target_job_titles) {
        stepData.step2.target_job_titles.forEach((title: string) => this.selectedJobTitles.add(title));
        // Saved custom entries mean 'Other' was in use — keep its chip
        // active so the free-text input stays visible.
        if (Array.from(this.selectedJobTitles).some(t => !isPredefinedOption(t, this.targetJobTitleOptions))) {
          this.selectedJobTitles.add(OTHER_OPTION_ID);
        }
      }
    }
  }

  private subscribeToCompleteSuccess(): void {
    const completeSub = this.completeSuccess$
      .pipe(filter(success => success))
      .subscribe(() => {
        // Navigation handled by effect
      });
    this.subscriptions.add(completeSub);
  }

  // ─── Data Loading ───────────────────────────────────────────────────

  private loadCountries(): void {
    const url = `${environment.api.url}/simplified-onboarding/countries`;

    const countriesSub = this.http.get<any>(url).subscribe({
      next: (response) => {
        this.countries = response.data || [];
      },
      error: (err) => {
        console.error('[Onboarding] Failed to load countries:', err);
        this.countries = [];
      }
    });
    this.subscriptions.add(countriesSub);
  }

  private loadIndustries(): void {
    const url = `${environment.api.url}/simplified-onboarding/industries`;

    const industriesSub = this.http.get<any>(url).subscribe({
      next: (response) => {
        const rawIndustries = response.data || [];
        // Normalize: if items are strings, convert to {id, name} objects
        this.industries = rawIndustries.map((item: any) => {
          if (typeof item === 'string') {
            return { id: item, name: item };
          }
          return { id: item.id || item.name, name: item.name || item.label || item };
        });
      },
      error: (err) => {
        console.error('[Onboarding] Failed to load industries:', err);
        this.industries = [];
      }
    });
    this.subscriptions.add(industriesSub);
  }

  // ─── Step Navigation ────────────────────────────────────────────────

  nextStep(): void {
    if (this.step === 1) {
      if (!this.isStep1Valid()) {
        this.form.get('website_url')?.markAsTouched();
        return;
      }

      // Save step 1 data
      const step1Data = {
        website_url: this.form.get('website_url')?.value,
        company_name: this.form.get('company_name')?.value || ''
      };
      this.store.dispatch(OnboardingActions.updateOnboardingStep({ step: 1, data: step1Data }));

      // Trigger product extraction if not already running
      this.extractionStatus$.pipe(take(1)).subscribe(status => {
        if (!status || status === 'failed') {
          this.store.dispatch(OnboardingActions.extractProduct({
            websiteUrl: step1Data.website_url,
            companyName: step1Data.company_name
          }));
        }
      });

      this.step = 2;
    }
  }

  previousStep(): void {
    if (this.step > 1) {
      this.step--;
    }
  }

  // ─── Step 1: Website URL ────────────────────────────────────────────

  isStep1Valid(): boolean {
    return this.form.get('website_url')?.valid || false;
  }

  // ─── Step 2: Targeting ──────────────────────────────────────────────

  toggleJobTitle(title: string): void {
    if (this.selectedJobTitles.has(title)) {
      this.selectedJobTitles.delete(title);
    } else {
      this.selectedJobTitles.add(title);
    }
    this.syncJobTitlesToForm();
  }

  isJobTitleSelected(title: string): boolean {
    return this.selectedJobTitles.has(title);
  }

  removeJobTitle(title: string): void {
    this.selectedJobTitles.delete(title);
    this.syncJobTitlesToForm();
  }

  // ── Custom "Other" job titles (free text, 255 max each) ──
  readonly maxCustomTargetLength = MAX_CUSTOM_TARGET_LENGTH;
  customJobTitleInput = '';

  get isOtherJobTitleSelected(): boolean {
    return this.selectedJobTitles.has(OTHER_OPTION_ID);
  }

  addCustomJobTitle(): void {
    const value = sanitizeCustomTarget(this.customJobTitleInput);
    this.customJobTitleInput = '';

    const lower = value.toLowerCase();
    if (!value || Array.from(this.selectedJobTitles).some(t => t.toLowerCase() === lower)) {
      return;
    }

    this.selectedJobTitles.add(value);
    this.syncJobTitlesToForm();
  }

  private syncJobTitlesToForm(): void {
    // The 'Other' chip only reveals the custom input — it is never saved as
    // a target itself.
    this.form.patchValue({
      target_job_titles: Array.from(this.selectedJobTitles)
        .filter(t => t.toLowerCase() !== OTHER_OPTION_ID.toLowerCase())
    });
  }

  handleIndustrySelectionChange(selectedIndustries: any[]): void {
    const selectedNames = selectedIndustries.map(i => i.name || i.id || i);
    this.form.patchValue({
      target_industries: selectedNames
    });
  }

  handleCountrySelectionChange(selectedCountries: any[]): void {
    const selectedNames = selectedCountries.map(c => c.name || c.id || c);
    this.form.patchValue({
      target_countries: selectedNames
    });
  }

  // ─── Completion ─────────────────────────────────────────────────────

  completeOnboarding(): void {
    const step2Data: OnboardingTargetingData = {
      target_industries: this.form.get('target_industries')?.value || [],
      target_job_titles: this.form.get('target_job_titles')?.value || [],
      target_countries: this.form.get('target_countries')?.value || []
    };

    const missing: string[] = [];
    if (!step2Data.target_industries.length) missing.push('industries');
    if (!step2Data.target_job_titles.length) missing.push('job titles');
    if (!step2Data.target_countries.length) missing.push('countries');

    if (missing.length > 0) {
      this.popupService.notify(
        `Please select at least one option for: ${missing.join(', ')}.`,
        'Validation',
        'warning'
      );
      return;
    }

    // Targeting rides along with the completion request. Saving it through a separate
    // update-step dispatch raced the completion call, so the backend often built the ICP
    // and the lead run from empty targeting.
    this.store.dispatch(OnboardingActions.completeOnboarding({ data: step2Data }));
  }

  // ─── Extraction Badge Helpers ───────────────────────────────────────

  getExtractionBadgeText(status: ExtractionStatus | null): string {
    switch (status) {
      case 'pending':
        return 'Starting analysis...';
      case 'processing':
        return 'Analyzing website...';
      case 'completed':
        return 'Analysis complete';
      case 'failed':
        return 'Analysis failed';
      default:
        return '';
    }
  }

  getExtractionBadgeClass(status: ExtractionStatus | null): string {
    switch (status) {
      case 'pending':
      case 'processing':
        return 'badge-processing';
      case 'completed':
        return 'badge-success';
      case 'failed':
        return 'badge-error';
      default:
        return '';
    }
  }

  // ─── Progress ───────────────────────────────────────────────────────

  getStepTitle(): string {
    return `Step ${this.step} of 2`;
  }

  getProgressDots(): boolean[] {
    return [this.step >= 1, this.step >= 2];
  }

  isContinueDisabled(): boolean {
    if (this.step === 1) return !this.isStep1Valid();
    return false;
  }
}
